import { Controller, FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';
import { Selection, TmdxSelect } from '@ds';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import {
  checkSupplementalProviderError,
  WarningTypeEnum,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { renderSelectItem } from '@/utils/helpers';
import { SelectItemType } from '@/types';

type ProviderSelectProps<T extends FieldValues> = {
  providerFieldName: Path<T>;
  providerInputTitle: string;
  providerList: SelectItemType[];
  selectedSupplementalProvider?: string;
};

export const ProviderSelect = <T extends FieldValues>({
  providerFieldName,
  providerInputTitle,
  selectedSupplementalProvider,
  providerList,
}: ProviderSelectProps<T>) => {
  const { mode, routeType } = useLegFormContext();
  const { legs } = useRouteDetails(routeType);
  const { isMainDataEdit, addErrorMessage, removeErrorMessage } = useLegFormContext();
  const { control, setValue } = useFormContext<T>();

  const handleProviderSelect = (provider: Selection) => {
    const selectedProvider = Array.from(provider)[0] as PathValue<T, Path<T>>;

    setValue(providerFieldName, selectedProvider);

    if (selectedSupplementalProvider) {
      const showError = checkSupplementalProviderError(
        mode,
        legs,
        selectedSupplementalProvider,
        selectedProvider as string,
      );

      if (showError) {
        addErrorMessage(WarningTypeEnum.SupplementalProviderCombination);
      } else {
        removeErrorMessage(WarningTypeEnum.SupplementalProviderCombination);
      }
    }
  };

  return (
    <Controller
      name={providerFieldName}
      control={control}
      render={({ field: { value, ...field }, fieldState: { invalid } }) => (
        <TmdxSelect
          placeholder={providerInputTitle}
          label={`${providerInputTitle} *`}
          aria-label={providerInputTitle}
          selectedKeys={[value]}
          isInvalid={invalid}
          disallowEmptySelection
          items={providerList}
          isDisabled={!isMainDataEdit}
          onSelectionChange={handleProviderSelect}
          {...field}
        >
          {renderSelectItem}
        </TmdxSelect>
      )}
    />
  );
};
